import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "../../../axios";

const PAGE_SIZE = 20;
const displayValue = (value) =>
  typeof value === "string" || typeof value === "number" ? value : "-";

const formatAmount = (value, currency = "INR") => {
  if (value === null || value === undefined || value === "") return "-";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency }).format(amount);
  } catch {
    return `${amount} ${displayValue(currency)}`;
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("en-IN");
};

const extractPayments = (response) => {
  if (response?.success === false) throw new Error(response.message || "Unable to load institute payments.");
  const payload = response?.result;
  const payments = payload?.items;
  if (!Array.isArray(payments)) throw new Error("Unable to read institute payments from the response.");
  const pagination = payload?.pagination;
  const count = Number(pagination?.totalPages ?? 1);
  return {
    payments,
    totalPages: Number.isFinite(count) ? Math.max(1, count) : 1,
    totalItems: pagination?.totalItems,
    totalAmount: payload?.totalAmount,
  };
};

const InstitutePayments = () => {
  const [page, setPage] = useState(1);
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: ["institute-payments", page],
    queryFn: async ({ signal }) => {
      const response = await axios.get("/institute/payments", { params: { page, limit: PAGE_SIZE }, signal });
      return extractPayments(response.data);
    },
    retry: false,
  });
  const payments = data?.payments ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="min-h-screen bg-gray-50 p-4 pt-10 sm:p-6 sm:pt-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Institute Payment</h1>
          <p className="mt-1 text-sm text-gray-500">View institute payment records.</p>
        </div>
        <button type="button" onClick={() => refetch()} disabled={isFetching} className="rounded-lg bg-purple px-4 py-2 font-medium text-white disabled:opacity-50">
          {isFetching ? "Refreshing..." : "Refresh"}
        </button>
      </div>
      {error ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          {error.response?.data?.message || error.message || "Failed to fetch institute payments."}
          <button type="button" onClick={() => refetch()} disabled={isFetching} className="ml-3 underline disabled:opacity-50">Retry</button>
        </div>
      ) : isPending ? (
        <p role="status" className="p-8 text-center text-gray-500">Loading institute payments...</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-4">
            <div className="rounded-lg bg-white px-5 py-4 shadow-sm"><p className="text-sm text-gray-500">Total Payments</p><p className="mt-1 text-xl font-bold text-gray-800">{displayValue(data?.totalItems)}</p></div>
            <div className="rounded-lg bg-white px-5 py-4 shadow-sm"><p className="text-sm text-gray-500">Total Amount</p><p className="mt-1 text-xl font-bold text-gray-800">{formatAmount(data?.totalAmount)}</p></div>
          </div>
          <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">Institute payment list</caption>
              <thead className="bg-gray-100 text-gray-600">
                <tr>{["Sr No.", "Institute", "Owner", "Plan", "Amount", "Status", "Order ID", "Gateway", "Gateway Payment ID", "Completed On", "Created On"].map((title) => (
                  <th key={title} scope="col" className="whitespace-nowrap px-4 py-3">{title}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.length === 0 ? (
                  <tr><td colSpan={11} className="p-8 text-center text-gray-500">No institute payments found.</td></tr>
                ) : payments.map((record, index) => {
                  const institute = record?.instituteOwnerId?.institute;
                  const owner = record?.instituteOwnerId?.owner;
                  const status = displayValue(record?.status);
                  const statusClass = ["PAID", "SUCCESS", "COMPLETED"].includes(String(status).toUpperCase())
                    ? "bg-green-50 text-green-700"
                    : ["FAILED", "CANCELLED"].includes(String(status).toUpperCase())
                      ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-700";
                  return (
                    <tr key={record?._id ?? record?.id ?? index}>
                      <td className="px-4 py-3">{(page - 1) * PAGE_SIZE + index + 1}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{displayValue(institute?.name)}</p>
                        <p className="text-xs text-gray-500">{displayValue(record?.instituteId)}</p>
                        <p className="text-xs text-gray-500">{displayValue(institute?.email)}</p>
                      </td>
                      <td className="px-4 py-3"><p>{displayValue(owner?.name)}</p><p className="text-xs text-gray-500">{displayValue(owner?.email)}</p><p className="text-xs text-gray-500">{displayValue(owner?.mobile)}</p></td>
                      <td className="px-4 py-3"><p>{displayValue(record?.planSnapshot?.name)}</p><p className="text-xs text-gray-500">{displayValue(record?.planSnapshot?.billingCycle)}</p></td>
                      <td className="whitespace-nowrap px-4 py-3">{formatAmount(record?.amount)}</td>
                      <td className="px-4 py-3"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>{status}</span></td>
                      <td className="break-all px-4 py-3">{displayValue(record?.orderId)}</td>
                      <td className="px-4 py-3">{displayValue(record?.gateway)}</td>
                      <td className="break-all px-4 py-3">{displayValue(record?.gatewayPaymentId)}</td>
                      <td className="whitespace-nowrap px-4 py-3">{formatDate(record?.completedOn)}</td>
                      <td className="whitespace-nowrap px-4 py-3">{formatDate(record?.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <nav aria-label="Institute payment pagination" className="mt-4 flex items-center justify-end gap-4 text-sm">
            <button type="button" onClick={() => setPage((current) => current - 1)} disabled={page <= 1 || isFetching} className="rounded-lg border px-4 py-2 disabled:opacity-50">Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button type="button" onClick={() => setPage((current) => current + 1)} disabled={page >= totalPages || isFetching} className="rounded-lg border px-4 py-2 disabled:opacity-50">Next</button>
          </nav>
        </>
      )}
    </div>
  );
};

export default InstitutePayments;
